import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { teamsApi, foldersApi, actionCardsApi } from '@/utils/api';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { relTime } from '@/utils/time';
import { taskRYG, aggregateRYG, rygFromTasks, RYG_META, RYG_ORDER, todayStr } from '@/utils/ryg';
import InlineTaskPanel from '@/components/InlineTaskPanel';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import InviteOnCreateDialog from '@/components/InviteOnCreateDialog';
import { Users, UserPlus, Loader2, Crown, X, Mail, Clock, Check, FileText, Inbox, Plus, ChevronLeft, ChevronRight, ChevronDown, Trash2, CheckSquare, Pin, Pencil, Activity, MessageSquare, AlertTriangle, Folder, MoreHorizontal } from 'lucide-react';

function initials(name) {
  return (name || '?').trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const PAGE_SIZE = 15;

const FOLDER_COLORS = ['#b08900', '#6994c6', '#5a7d52', '#B4332E', '#8a6fb0', '#3d9e9e', '#c2553f'];
const FOLDER_EMOJIS = ['📁', '⭐', '🚀', '💼', '🎯', '🔥', '💡', '📌', '✅', '🐛', '📣', '🎨', '💰', '📈'];
const folderColorOf = (f) => (f && f.color) || '#b08900';

function Pager({ page, total, onChange, testid }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  const cur = Math.min(page, pages);
  const start = (cur - 1) * PAGE_SIZE + 1;
  const end = Math.min(cur * PAGE_SIZE, total);
  return (
    <div className="flex items-center justify-between px-3 py-2.5 border-t border-border/60" data-testid={testid}>
      <span className="text-xs text-muted-foreground">{start}–{end} of {total}</span>
      <div className="flex items-center gap-2">
        <button disabled={cur <= 1} onClick={() => onChange(cur - 1)} data-testid={`${testid}-prev`}
          className="p-1 rounded-lg disabled:opacity-40 hover:bg-secondary"><ChevronLeft size={16} /></button>
        <span className="text-xs font-medium">{cur} / {pages}</span>
        <button disabled={cur >= pages} onClick={() => onChange(cur + 1)} data-testid={`${testid}-next`}
          className="p-1 rounded-lg disabled:opacity-40 hover:bg-secondary"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

function CollapsibleSection({ id, title, actions, children, defaultOpen = true }) {
  const [open, setOpen] = useState(() => {
    const v = localStorage.getItem(`mk_ws_sec_${id}`);
    return v === null ? defaultOpen : v === '1';
  });
  const toggle = () => setOpen(o => { const n = !o; localStorage.setItem(`mk_ws_sec_${id}`, n ? '1' : '0'); return n; });
  return (
    <div className="ws-sec" data-testid={`workspace-section-${id}`}>
      <div className="sec-h ws-sec-h" role="button" onClick={toggle} data-testid={`workspace-section-toggle-${id}`}>
        <span className="lab flex items-center gap-1.5">
          <ChevronDown size={15} className="ws-caret" style={{ transform: open ? 'none' : 'rotate(-90deg)' }} />
          {title}
        </span>
        {actions && <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>{actions}</div>}
      </div>
      {open && <div data-testid={`workspace-section-body-${id}`}>{children}</div>}
    </div>
  );
}

export default function WorkspacePage() {
  const { user } = useAuth();
  const { openTaskDrawer } = useTaskDrawer();
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [wsTasks, setWsTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [taskPage, setTaskPage] = useState(1);
  const [activityPage, setActivityPage] = useState(1);
  const [memberPage, setMemberPage] = useState(1);
  const [myActivity, setMyActivity] = useState([]);
  const [showAllWs, setShowAllWs] = useState(false);
  const [wsSort, setWsSort] = useState(() => localStorage.getItem('mk_ws_sort') || 'busiest');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [focusMember, setFocusMember] = useState(null);
  const [reassignTo, setReassignTo] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [pinBusy, setPinBusy] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [cardFilter, setCardFilter] = useState('all');
  const [wsFolders, setWsFolders] = useState([]);
  const [wsFolder, setWsFolder] = useState(null);
  const [showAllCards, setShowAllCards] = useState(false);
  const [legendFilter, setLegendFilter] = useState(null);
  const [wsStatusFilter, setWsStatusFilter] = useState(null); // Home RYG filter: null|'red'|'yellow'|'green'
  const [rygFilter, setRygFilter] = useState(null); // Hub task RYG filter
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [draggingCard, setDraggingCard] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [selectedCards, setSelectedCards] = useState([]);
  const [bulkMoveTo, setBulkMoveTo] = useState('');
  const [cardSort, setCardSort] = useState('recent');
  const [digestBusy, setDigestBusy] = useState(false);
  const [activitySeenAt, setActivitySeenAt] = useState('');
  const [wsSearch, setWsSearch] = useState('');
  const [wsRole, setWsRole] = useState('all'); // 'all' | 'owner' | 'member'
  const [wsMenu, setWsMenu] = useState(null); // 'role' | 'sort'
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(false);
  const [subfolderParent, setSubfolderParent] = useState(null);
  const [subfolderName, setSubfolderName] = useState('');
  const [creatingSubfolder, setCreatingSubfolder] = useState(false);

  useEffect(() => {
    if (!wsMenu) return;
    const onDown = (e) => { if (!e.target.closest('[data-menuroot]')) setWsMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') setWsMenu(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [wsMenu]);

  const load = useCallback(async (keepSelection = false) => {
    try {
      const [tRes, iRes] = await Promise.all([teamsApi.list(), teamsApi.invitations()]);
      const ts = tRes.data.teams || [];
      setTeams(ts);
      setInvitations(iRes.data.invitations || []);
      if (!keepSelection) setSelectedId(prev => (prev && ts.some(t => t.id === prev)) ? prev : null);
    } catch { toast.error('Failed to load workspace'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeTeam = teams.find(t => t.id === selectedId);
  const isViewer = activeTeam?.my_role === 'viewer';

  // Load activity + tasks for the selected workspace.
  useEffect(() => {
    if (!selectedId) { setActivity([]); setWsTasks([]); setMyActivity([]); return; }
    setTaskPage(1); setActivityPage(1); setMemberPage(1); setShowAllTasks(false);
    setActivityFilter('all'); setCardFilter('all'); setFocusMember(null); setWsFolder(null); setSelectedCards([]); setShowAllCards(false); setLegendFilter(null);
    setActivitySeenAt(localStorage.getItem(`mk_ws_act_seen_${selectedId}`) || '');
    teamsApi.activity(selectedId).then(a => setActivity(a.data.cards || [])).catch(() => setActivity([]));
    foldersApi.list().then(r => setWsFolders((r.data.folders || []).filter(f => f.scope === 'team' && f.team_id === selectedId))).catch(() => setWsFolders([]));
    teamsApi.tasks(selectedId).then(r => setWsTasks(r.data.tasks || [])).catch(() => setWsTasks([]));
    teamsApi.myActivity(selectedId).then(r => setMyActivity(r.data.activity || [])).catch(() => setMyActivity([]));
  }, [selectedId]);

  const reloadHub = useCallback(() => {
    if (!selectedId) return;
    teamsApi.tasks(selectedId).then(r => setWsTasks(r.data.tasks || [])).catch(() => {});
    teamsApi.myActivity(selectedId).then(r => setMyActivity(r.data.activity || [])).catch(() => {});
  }, [selectedId]);

  // Auto-open a specific workspace via router state ({ openTeam }) or ?team=<id> (e.g. from the
  // dashboard or an assignment notification link).
  useEffect(() => {
    const openTeam = location.state?.openTeam || new URLSearchParams(location.search).get('team');
    if (openTeam && teams.some(t => t.id === openTeam)) {
      setSelectedId(openTeam);
      navigate('/workspace', { replace: true, state: {} });
    }
  }, [teams, location.state, location.search, navigate]);

  const [highlightInvites, setHighlightInvites] = useState(false);
  useEffect(() => {
    if (loading || location.hash !== '#invitations') return;
    const el = document.getElementById('invitations');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightInvites(true);
    const t = setTimeout(() => setHighlightInvites(false), 1600);
    return () => clearTimeout(t);
  }, [loading, location.hash, invitations.length]);

  const [inviteTeam, setInviteTeam] = useState(null);
  const createWorkspace = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await teamsApi.create(newName.trim());
      toast.success('Workspace created');
      setInviteTeam({ id: res.data.id, name: newName.trim() });
      setNewName(''); setShowCreate(false);
      await load(true);
      setSelectedId(res.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create workspace'); }
    finally { setCreating(false); }
  };

  const togglePin = async (t, e) => {
    e?.stopPropagation();
    setPinBusy(t.id);
    try {
      if (t.pinned) await teamsApi.unpin(t.id); else await teamsApi.pin(t.id);
      setTeams(prev => prev.map(x => x.id === t.id ? { ...x, pinned: !x.pinned } : x));
    } catch { toast.error('Could not update pin'); }
    finally { setPinBusy(''); }
  };

  const saveTitle = async () => {
    const name = titleDraft.trim();
    if (!name || !activeTeam || name === activeTeam.name) { setEditingTitle(false); return; }
    try {
      await teamsApi.rename(activeTeam.id, name);
      setTeams(prev => prev.map(x => x.id === activeTeam.id ? { ...x, name } : x));
      toast.success('Workspace renamed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename'); }
    finally { setEditingTitle(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim() || !activeTeam) return;
    setInviting(true);
    try {
      const res = await teamsApi.invite(activeTeam.id, email.trim(), inviteRole);
      toast.success(res.data.message || 'Invitation sent');
      setEmail('');
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  const bulkReassign = async () => {
    if (!focusMember || !reassignTo) return;
    setReassigning(true);
    try {
      const res = await teamsApi.reassign(activeTeam.id, { from_user_id: focusMember.user_id, to_user_id: reassignTo });
      const toName = res.data.to_name || 'the member';
      toast.success(`Reassigned ${res.data.reassigned} task${res.data.reassigned === 1 ? '' : 's'} to ${toName}`);
      setReassignTo('');
      setFocusMember(null);
      reloadHub();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reassign tasks'); }
    finally { setReassigning(false); }
  };

  const deleteWorkspace = async () => {
    if (!activeTeam || deleteConfirm.trim() !== activeTeam.name) return;
    const t = activeTeam;
    setDeleting(true);
    try {
      await teamsApi.remove(t.id, deleteConfirm.trim());
      toast.success(`Workspace “${t.name}” moved to Trash`, {
        description: 'You can restore it from Home for 30 days.',
        action: { label: 'Undo', onClick: async () => { try { await teamsApi.restore(t.id); toast.success('Workspace restored'); load(true); } catch { toast.error('Could not restore workspace'); } } },
      });
      setShowDelete(false);
      setDeleteConfirm('');
      setSelectedId(null);
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete workspace'); }
    finally { setDeleting(false); }
  };

  const remove = async (teamId, userId) => {
    try { await teamsApi.removeMember(teamId, userId); await load(true); toast.success('Member removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };

  const respond = async (teamId, accept) => {
    setBusy(teamId);
    try {
      if (accept) { const r = await teamsApi.accept(teamId); toast.success(`Joined ${r.data.joined}`); }
      else { await teamsApi.decline(teamId); toast.success('Invitation declined'); }
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(''); }
  };

  const addWorkspaceTask = async (e) => {
    e.preventDefault();
    const title = newTaskTitle.trim();
    if (!title || !activeTeam) return;
    setAddingTask(true);
    try {
      const res = await teamsApi.createTask(activeTeam.id, { title });
      setWsTasks(prev => [res.data, ...prev]);
      setNewTaskTitle('');
      setShowAddTask(false);
      toast.success('Task added to workspace');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add task'); }
    finally { setAddingTask(false); }
  };

  const toggleWorkspaceTask = async (t) => {
    const status = t.status === 'done' ? 'not_started' : 'done';
    try {
      const res = await teamsApi.updateTask(activeTeam.id, t.id, { status });
      setWsTasks(prev => prev.map(x => x.id === t.id ? res.data : x));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update task'); }
  };

  const deleteWorkspaceTask = async (t) => {
    if (!window.confirm('Delete this workspace task?')) return;
    try {
      await teamsApi.deleteTask(activeTeam.id, t.id);
      setWsTasks(prev => prev.filter(x => x.id !== t.id));
      toast.success('Task deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete task'); }
  };

  const wsAssigneeIds = (t) => (t.assignees?.length ? t.assignees.map(a => a.user_id) : (t.assignee_user_id ? [t.assignee_user_id] : [])).filter(Boolean);
  const assignWorkspaceTask = async (t, sel) => {
    try {
      const res = await teamsApi.assignTask(activeTeam.id, t.id, sel);
      setWsTasks(prev => prev.map(x => x.id === t.id ? res.data : x));
      teamsApi.myActivity(activeTeam.id).then(r => setMyActivity(r.data.activity || [])).catch(() => {});
      const n = Array.isArray(sel) ? sel.length : (sel ? 1 : 0);
      toast.success(n ? 'Assignees updated' : 'Unassigned');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign task'); }
  };
  const toggleWsAssignee = (t, uid) => {
    const cur = wsAssigneeIds(t);
    assignWorkspaceTask(t, cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid]);
  };

  if (loading) return <div className="pagewrap"><div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div></div>;

  // ── Invitations banner (shown on the list view) ──
  const invitesBanner = invitations.length > 0 && (
    <div id="invitations" className="rounded-2xl border p-5 mb-5" style={{ borderColor: '#fcd34d', background: '#fffbeb', boxShadow: highlightInvites ? '0 0 0 3px rgba(212,164,74,0.55)' : 'none', transition: 'box-shadow .3s ease' }} data-testid="pending-invitations">
      <p className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: '#92400e' }}>Workspace invitations</p>
      <div className="space-y-2">
        {invitations.map(inv => (
          <div key={inv.team_id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-border" data-testid={`invitation-${inv.team_id}`}>
            <Users size={18} style={{ color: '#6994c6' }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{inv.name}</p>
              <p className="text-xs text-muted-foreground">{inv.member_count} member{inv.member_count === 1 ? '' : 's'} · invited {relTime(inv.invited_at)}</p>
            </div>
            <button onClick={() => respond(inv.team_id, true)} disabled={busy === inv.team_id} data-testid={`accept-invitation-${inv.team_id}`}
              className="inline-flex items-center gap-1 px-3 h-8 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ background: '#5a7d52' }}>
              {busy === inv.team_id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}Accept
            </button>
            <button onClick={() => respond(inv.team_id, false)} disabled={busy === inv.team_id} data-testid={`decline-invitation-${inv.team_id}`}
              className="px-3 h-8 rounded-lg text-xs font-medium border" style={{ color: '#B4332E', borderColor: 'rgba(180,51,46,0.3)' }}>Decline</button>
          </div>
        ))}
      </div>
    </div>
  );

  // ══════════════ LIST VIEW (first view) ══════════════
  if (!activeTeam) {
    // Home is now the aggregate workspaces view; the standalone list redirects there.
    // The hub (activeTeam) is still reachable via router state / ?team=<id>.
    const pendingOpen = location.state?.openTeam || new URLSearchParams(location.search).get('team');
    if (!loading && !pendingOpen) {
      return <Navigate to="/" replace />;
    }
    if (loading || pendingOpen) {
      return <div className="pagewrap"><Skeleton className="h-10 w-64 mb-4" /></div>;
    }
    const sortTeams = (arr) => [...arr].sort((a, b) => {
      if (wsSort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (wsSort === 'recent') return (b.last_activity_at || '').localeCompare(a.last_activity_at || '');
      return (b.card_count || 0) - (a.card_count || 0);
    });
    const teamRYG = (t) => aggregateRYG({ red: t.overdue_tasks || 0, yellow: (t.open_tasks || 0) - (t.overdue_tasks || 0) });
    const q = wsSearch.trim().toLowerCase();
    const matches = (t) => (!q || (t.name || '').toLowerCase().includes(q))
      && (wsRole === 'all' || (wsRole === 'owner' ? t.is_owner : !t.is_owner))
      && (!wsStatusFilter || teamRYG(t) === wsStatusFilter);
    const pinnedTeams = sortTeams(teams.filter(t => t.pinned && matches(t)));
    const unpinnedTeams = sortTeams(teams.filter(t => !t.pinned && matches(t)));
    const remainingSlots = Math.max(0, 10 - pinnedTeams.length);
    const visibleUnpinned = showAllWs ? unpinnedTeams : unpinnedTeams.slice(0, remainingSlots);
    const displayTeams = [...pinnedTeams, ...visibleUnpinned];
    const hiddenCount = unpinnedTeams.length - visibleUnpinned.length;
    const AV_COLORS = ['blue', 'green', 'butter', 'clay', 'ink'];
    const SORT_LABEL = { busiest: 'Busiest', recent: 'Most recent', name: 'Name' };
    const ROLE_LABEL = { all: 'All roles', owner: 'Owned by me', member: 'Member' };
    const rygCounts = teams.reduce((a, t) => { a[teamRYG(t)] += 1; return a; }, { red: 0, yellow: 0, green: 0 });
    const rollup = teams.reduce((a, t) => ({
      cards: a.cards + (t.card_count || 0),
      open: a.open + (t.open_tasks || 0),
      overdue: a.overdue + (t.overdue_tasks || 0),
    }), { cards: 0, open: 0, overdue: 0 });
    return (
      <div className="pagewrap" data-testid="workspace-page">
        <div className="page-head">
          <div>
            <h1>Workspaces</h1>
            <p className="sub">Collaborate with different teams — each workspace has its own members</p>
          </div>
          <button onClick={() => setShowCreate(v => !v)} data-testid="new-workspace-button" className="btn-new">
            <Plus size={15} />New workspace
          </button>
        </div>

        {teams.length > 0 && (
          <div className="ws-rollup" data-testid="workspace-rollup">
            <div className="ws-rollup-item"><span className="k">Workspaces</span><span className="v" data-testid="rollup-workspaces">{teams.length}</span></div>
            <div className="ws-rollup-item"><span className="k">Cards</span><span className="v" data-testid="rollup-cards">{rollup.cards}</span></div>
            <div className="ws-rollup-item"><span className="k">Open tasks</span><span className="v" data-testid="rollup-open">{rollup.open}</span></div>
            <div className="ws-rollup-item"><span className="k">Overdue</span><span className={`v${rollup.overdue > 0 ? ' over' : ''}`} data-testid="rollup-overdue">{rollup.overdue}</span></div>
          </div>
        )}

        {teams.length > 0 && (
          <div className="ryg-bar" data-testid="workspace-ryg-bar">
            <span className="ryg-lbl">Status</span>
            {RYG_ORDER.map(k => (
              <button key={k} className={`ryg-chip${wsStatusFilter === k ? ' on' : ''}`} data-testid={`workspace-ryg-${k}`}
                onClick={() => setWsStatusFilter(wsStatusFilter === k ? null : k)}
                title={wsStatusFilter === k ? 'Clear filter' : `Show ${RYG_META[k].label.toLowerCase()} workspaces`}
                style={wsStatusFilter === k ? { borderColor: RYG_META[k].color, background: RYG_META[k].bg } : undefined}>
                <span className="ryg-dot" style={{ background: RYG_META[k].color }} />
                {RYG_META[k].label}<b>{rygCounts[k]}</b>
              </button>
            ))}
            {wsStatusFilter && <button className="ryg-clear" data-testid="workspace-ryg-clear" onClick={() => setWsStatusFilter(null)}><X size={13} />Clear</button>}
          </div>
        )}

        {showCreate && (
          <form onSubmit={createWorkspace} className="flex gap-2 mb-4 rounded-2xl border border-border bg-white p-4" data-testid="new-workspace-form">
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="Workspace name (e.g. Acme Sales)"
              data-testid="new-workspace-input"
              className="flex-1 h-11 px-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-[#6FAE8F]" />
            <button type="submit" disabled={creating || !newName.trim()} data-testid="new-workspace-submit" className="btn-green inline-flex items-center gap-2">
              {creating ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}Create
            </button>
          </form>
        )}

        {invitesBanner}

        {/* Toolbar */}
        <div className="toolbar">
          <div className="tool-search" style={{ maxWidth: 'none' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input value={wsSearch} onChange={e => setWsSearch(e.target.value)} placeholder="Search workspaces"
              data-testid="workspace-search" className="w-full border-0 bg-transparent outline-none text-sm" />
          </div>
          <div className="tool-right">
            <div className="dd" data-menuroot>
              <button className={`tbtn${wsRole !== 'all' ? ' on' : ''}`} data-testid="workspace-role-btn"
                onClick={() => setWsMenu(m => m === 'role' ? null : 'role')}>
                <Users size={15} />{ROLE_LABEL[wsRole]}
              </button>
              <div className={`menu dd-menu${wsMenu === 'role' ? ' open' : ''}`}>
                <div className="mlab">Filter by role</div>
                {Object.entries(ROLE_LABEL).map(([k, label]) => (
                  <button key={k} className={`mi${wsRole === k ? ' on' : ''}`} data-testid={`workspace-role-${k}`}
                    onClick={() => { setWsRole(k); setWsMenu(null); }}>{label}<span className="mcheck"><Check size={14} /></span></button>
                ))}
              </div>
            </div>
            <div className="dd" data-menuroot>
              <button className="tbtn" data-testid="workspace-sort-btn"
                onClick={() => setWsMenu(m => m === 'sort' ? null : 'sort')}>
                <Activity size={15} />Sort: {SORT_LABEL[wsSort]}
              </button>
              <div className={`menu dd-menu${wsMenu === 'sort' ? ' open' : ''}`}>
                <div className="mlab">Sort workspaces by</div>
                {Object.entries(SORT_LABEL).map(([k, label]) => (
                  <button key={k} className={`mi${wsSort === k ? ' on' : ''}`} data-testid={`workspace-sort-${k}`}
                    onClick={() => { setWsSort(k); localStorage.setItem('mk_ws_sort', k); setWsMenu(null); }}>{label}<span className="mcheck"><Check size={14} /></span></button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {displayTeams.length === 0 ? (
          <div className="list-wrap"><div className="ac-empty" data-testid="workspace-empty">
            <div className="eic"><Users size={21} /></div>
            <div className="et">No workspaces{q || wsRole !== 'all' ? ' match' : ' yet'}</div>
            <div className="es">{q || wsRole !== 'all' ? 'Try clearing your search or filter' : 'Create a workspace to collaborate with your team'}</div>
          </div></div>
        ) : (
          <div className="ws-grid" data-testid="workspace-list">
            {displayTeams.map(t => {
              const pending = (t.pending_invites?.length || 0) + (t.pending_emails?.length || 0);
              const members = t.members || [];
              const shown = members.slice(0, 5);
              const extra = members.length - shown.length;
              return (
                <div key={t.id} onClick={() => setSelectedId(t.id)} data-testid={`workspace-card-open-${t.id}`}
                  role="button" tabIndex={0} className={`ws-card${t.pinned ? ' pinned' : ''}`}>
                  <div className="ws-top">
                    <div className={`ws-ic ${t.is_owner ? 'green' : 'blue'}`}><Users /></div>
                    <div className="ws-id">
                      <div className="ws-name" data-testid={`workspace-card-name-${t.id}`}>
                        <span className="ryg-dot lg" title={RYG_META[teamRYG(t)].label} data-testid={`workspace-ryg-dot-${t.id}`} style={{ background: RYG_META[teamRYG(t)].color }} />
                        <span className="nm-txt">{t.name}</span>
                        {t.is_owner && <span className="crown" title="You own this workspace"><Crown /></span>}
                      </div>
                      <div className="ws-meta">
                        <span>{members.length} member{members.length === 1 ? '' : 's'}</span>
                        {pending > 0 && <><span>·</span><span className="pend">{pending} pending</span></>}
                        <span>·</span><span className="role">{t.is_owner ? 'Owner' : 'Member'}</span>
                      </div>
                    </div>
                    <div className="ws-tools" onClick={e => e.stopPropagation()}>
                      <button className="icbtn" onClick={e => togglePin(t, e)} disabled={pinBusy === t.id} data-testid={`workspace-pin-${t.id}`}
                        title={t.pinned ? 'Unpin' : 'Pin to top'} style={{ color: t.pinned ? '#c79a3f' : undefined }}>
                        <Pin size={15} fill={t.pinned ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                  </div>
                  <div className="ws-people">
                    <div className="avs">
                      {shown.map((m, i) => (
                        <div key={m.user_id || i} className={`av ${AV_COLORS[i % AV_COLORS.length]}`} title={m.name || m.email}>{initials(m.name || m.email)}</div>
                      ))}
                      {extra > 0 && <div className="av ink" title={`${extra} more`}>+{extra}</div>}
                      {pending > 0 && <div className="av ghost" title={`${pending} invited`}>…</div>}
                    </div>
                  </div>
                  <div className="ws-stats">
                    <span className={`ws-stat${t.card_count > 0 ? ' hot' : ''}`} data-testid={`workspace-card-count-${t.id}`}>
                      <FileText /><b>{t.card_count || 0}</b> card{t.card_count === 1 ? '' : 's'}
                    </span>
                    <span className="ws-stat" data-testid={`workspace-open-tasks-${t.id}`}>
                      <CheckSquare /><b>{t.open_tasks || 0}</b> open
                    </span>
                    {t.overdue_tasks > 0 && (
                      <span className="ws-stat over" data-testid={`workspace-overdue-tasks-${t.id}`}><AlertTriangle /><b>{t.overdue_tasks}</b> overdue</span>
                    )}
                    {t.last_activity_at && <span className="ws-last">Active {relTime(t.last_activity_at)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {(hiddenCount > 0 || showAllWs) && (
          <div className="flex justify-center mt-4">
            <button onClick={() => setShowAllWs(v => !v)} data-testid="workspace-view-more"
              className="inline-flex items-center gap-1.5 px-4 h-9 rounded-xl text-sm font-medium border border-border bg-white hover:bg-secondary transition-colors">
              {showAllWs ? 'Show less' : `View more (${hiddenCount})`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ══════════════ HUB VIEW (single workspace) ══════════════
  // ── Folder scoping: when a folder is active, narrow the whole hub (tasks +
  // activity + cards) to that folder and everything nested beneath it. ──
  const folderById = {};
  wsFolders.forEach(f => { folderById[f.id] = f; });
  // Defensive: a card's folder_id may reference a folder outside this workspace
  // (legacy data / deleted folders). Treat those as unfiled so the card never vanishes.
  const wsFolderIdSet = new Set(wsFolders.map(f => f.id));
  const cfOf = (c) => (c.folder_id && wsFolderIdSet.has(c.folder_id)) ? c.folder_id : null;
  const descendantFolderIds = (fid) => {
    const out = [fid]; const stack = [fid];
    while (stack.length) { const cur = stack.pop(); wsFolders.forEach(f => { if ((f.parent_id || null) === cur) { out.push(f.id); stack.push(f.id); } }); }
    return out;
  };
  const branchFolderIds = wsFolder ? descendantFolderIds(wsFolder) : null;
  const cardFolderMap = {};
  activity.forEach(c => { cardFolderMap[c.id] = cfOf(c); });
  const inBranch = (t) => t.action_card_id && branchFolderIds.includes(cardFolderMap[t.action_card_id]);
  const scopedWsTasks = branchFolderIds ? wsTasks.filter(inBranch) : wsTasks;
  const branchTaskIds = new Set(scopedWsTasks.map(t => t.id));

  const mineTask = (t) => t.assignee_user_id === user?.id || (t.assignees || []).some(a => a?.user_id === user?.id) || (t.collaborators || []).some(c => c?.user_id === user?.id);
  const assignedTo = (t, uid) => t.assignee_user_id === uid || (t.assignees || []).some(a => a?.user_id === uid);
  const needsTasks = scopedWsTasks.filter(t => mineTask(t) && t.status !== 'done');
  const _today = todayStr();
  const hubRyg = rygFromTasks(scopedWsTasks, _today);
  const baseListTasks = focusMember
    ? scopedWsTasks.filter(t => assignedTo(t, focusMember.user_id))
    : (showAllTasks ? scopedWsTasks : needsTasks);
  const listTasks = rygFilter ? baseListTasks.filter(t => taskRYG(t, _today) === rygFilter) : baseListTasks;
  // Per-card RYG roll-up from the workspace's tasks (card = aggregate of its tasks).
  const cardRygCounts = {};
  wsTasks.forEach(t => {
    if (!t.action_card_id) return;
    const k = taskRYG(t, _today);
    (cardRygCounts[t.action_card_id] || (cardRygCounts[t.action_card_id] = { red: 0, yellow: 0, green: 0 }))[k] += 1;
  });
  const cardRYG = (cardId) => { const c = cardRygCounts[cardId]; return c ? aggregateRYG(c) : null; };
  const pagedTasks = listTasks.slice((Math.min(taskPage, Math.max(1, Math.ceil(listTasks.length / PAGE_SIZE))) - 1) * PAGE_SIZE, Math.min(taskPage, Math.max(1, Math.ceil(listTasks.length / PAGE_SIZE))) * PAGE_SIZE);
  const scopedActivity = branchFolderIds ? myActivity.filter(ev => branchTaskIds.has(ev.task_id)) : myActivity;
  const filteredActivity = activityFilter === 'all' ? scopedActivity : scopedActivity.filter(ev => ev.type === activityFilter);
  const pagedActivity = filteredActivity.slice((Math.min(activityPage, Math.max(1, Math.ceil(filteredActivity.length / PAGE_SIZE))) - 1) * PAGE_SIZE, Math.min(activityPage, Math.max(1, Math.ceil(filteredActivity.length / PAGE_SIZE))) * PAGE_SIZE);
  const unreadActivity = myActivity.filter(e => (e.at || '') > activitySeenAt && e.actor_name !== (user?.name || '')).length;
  const markActivitySeen = () => {
    if (unreadActivity === 0) return;
    const latest = myActivity[0]?.at || new Date().toISOString();
    localStorage.setItem(`mk_ws_act_seen_${selectedId}`, latest);
    setActivitySeenAt(latest);
  };
  const currentFolders = showAllCards ? [] : wsFolders.filter(f => (f.parent_id || null) === wsFolder);
  const noFolders = wsFolders.length === 0;
  const folderCardCount = (fid) => activity.filter(c => cfOf(c) === fid).length;
  const visibleCards = activity.filter(c => showAllCards || noFolders || cfOf(c) === wsFolder)
    .filter(c => !(showAllCards && legendFilter) || cfOf(c) === legendFilter)
    .filter(c => !rygFilter || cardRYG(c.id) === rygFilter)
    .filter(c => {
    if (cardFilter === 'open') return (c.open_tasks || 0) > 0;
    if (cardFilter === 'done') return (c.total_tasks || 0) > 0 && (c.open_tasks || 0) === 0;
    return true;
  }).sort((a, b) => {
    if (cardSort === 'tasks') return (b.total_tasks || 0) - (a.total_tasks || 0);
    if (cardSort === 'owner') return (a.owner_name || '').localeCompare(b.owner_name || '');
    return (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || '');
  });
  const openFolder = wsFolders.find(f => f.id === wsFolder);
  const folderChildren = (pid) => wsFolders.filter(f => (f.parent_id || null) === (pid || null));
  const selectFolder = (fid) => {
    setWsFolder(fid || null); setShowAllCards(false); setSelectedCards([]);
    setCardFilter('all'); setTaskPage(1); setActivityPage(1);
  };
  const breadcrumb = [];
  { let cur = wsFolder ? folderById[wsFolder] : null; while (cur) { breadcrumb.unshift(cur); cur = cur.parent_id ? folderById[cur.parent_id] : null; } }

  const renameFolder = async (f) => {
    const name = renameDraft.trim();
    if (!name || name === f.name) { setRenamingFolder(null); return; }
    try {
      await foldersApi.rename(f.id, name);
      setWsFolders(prev => prev.map(x => x.id === f.id ? { ...x, name } : x));
      toast.success('Folder renamed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename folder'); }
    finally { setRenamingFolder(null); }
  };

  const setFolderColor = async (f, color) => {
    const next = color === folderColorOf(f) ? null : color; // tap active swatch to clear
    setWsFolders(prev => prev.map(x => x.id === f.id ? { ...x, color: next } : x));
    try { await foldersApi.setColor(f.id, next); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not set color'); }
  };

  const setFolderEmoji = async (f, emoji) => {
    const next = emoji === f.emoji ? null : emoji; // tap active emoji to clear
    setWsFolders(prev => prev.map(x => x.id === f.id ? { ...x, emoji: next } : x));
    try { await foldersApi.setEmoji(f.id, next); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not set emoji'); }
  };

  const siblingFolders = wsFolder
    ? wsFolders.filter(f => (f.parent_id || null) === (openFolder?.parent_id || null) && f.id !== wsFolder)
    : [];

  const branchCardCount = (fid) => {
    const ids = descendantFolderIds(fid);
    return activity.filter(c => ids.includes(cfOf(c))).length;
  };

  const confirmDeleteFolder = async () => {
    const f = deleteFolderTarget;
    if (!f) return;
    setDeletingFolder(true);
    try {
      const res = await foldersApi.delete(f.id);
      const snapshot = res.data?.snapshot;
      const removed = new Set(descendantFolderIds(f.id));
      setWsFolders(prev => prev.filter(x => !removed.has(x.id)));
      setActivity(prev => prev.map(c => removed.has(c.folder_id) ? { ...c, folder_id: null } : c));
      if (removed.has(wsFolder)) selectFolder(f.parent_id && !removed.has(f.parent_id) ? f.parent_id : null);
      setDeleteFolderTarget(null);
      toast.success('Folder deleted — cards moved to workspace root', {
        action: snapshot ? { label: 'Undo', onClick: () => undoDeleteFolder(snapshot) } : undefined,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete folder'); }
    finally { setDeletingFolder(false); }
  };

  const undoDeleteFolder = async (snapshot) => {
    try {
      await foldersApi.restore({ folders: snapshot.folders || [], cards: snapshot.cards || [] });
      setWsFolders(prev => {
        const have = new Set(prev.map(x => x.id));
        const add = (snapshot.folders || []).filter(f => !have.has(f.id) && f.team_id === selectedId);
        return [...prev, ...add];
      });
      const cardFolder = {};
      (snapshot.cards || []).forEach(c => { cardFolder[c.id] = c.folder_id; });
      setActivity(prev => prev.map(c => cardFolder[c.id] !== undefined ? { ...c, folder_id: cardFolder[c.id] } : c));
      toast.success('Folder restored');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore folder'); }
  };

  const createSubfolder = async () => {
    const name = subfolderName.trim();
    if (!name || !subfolderParent || creatingSubfolder) return;
    setCreatingSubfolder(true);
    try {
      const res = await foldersApi.create({ name, scope: 'team', team_id: selectedId, parent_id: subfolderParent });
      setWsFolders(prev => [...prev, res.data]);
      const parent = subfolderParent;
      setSubfolderParent(null); setSubfolderName('');
      selectFolder(parent); // drop into the parent so the new subfolder shows as a chip
      toast.success(`Subfolder "${name}" created`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create subfolder'); }
    finally { setCreatingSubfolder(false); }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || creatingFolder) return;
    setCreatingFolder(true);
    try {
      const res = await foldersApi.create({ name, scope: 'team', team_id: selectedId, parent_id: wsFolder || null });
      setWsFolders(prev => [...prev, res.data]);
      setNewFolderName('');
      setNewFolderOpen(false);
      toast.success(`Folder "${name}" created`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create folder'); }
    finally { setCreatingFolder(false); }
  };

  const moveCards = async (ids, folderId) => {
    if (!ids.length) return;
    try {
      await Promise.all(ids.map(id => actionCardsApi.move(id, folderId, selectedId)));
      setActivity(prev => prev.map(c => ids.includes(c.id) ? { ...c, folder_id: folderId } : c));
      toast.success(`Moved ${ids.length} card${ids.length === 1 ? '' : 's'}${folderId ? ' to folder' : ' to root'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not move cards'); }
  };

  const moveCardToFolder = (cardId, folderId) => moveCards([cardId], folderId);

  const handleDrop = (folderId) => {
    setDropTarget(null);
    const id = draggingCard;
    setDraggingCard(null);
    if (id) moveCards([id], folderId);
  };

  const toggleSelect = (id) => setSelectedCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const bulkMove = async () => {
    await moveCards(selectedCards, bulkMoveTo || null);
    setSelectedCards([]);
    setBulkMoveTo('');
  };
  const isOverdue = (t) => {
    if (t.status === 'done' || !t.due_date) return false;
    const m = String(t.due_date).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] < new Date().toISOString().slice(0, 10) : false;
  };
  const isDueSoon = (t) => {
    if (t.status === 'done' || !t.due_date) return false;
    const m = String(t.due_date).match(/^(\d{4}-\d{2}-\d{2})/);
    if (!m) return false;
    const today = new Date().toISOString().slice(0, 10);
    const wk = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    return m[1] >= today && m[1] <= wk;
  };
  const workload = activeTeam.members.map(m => ({
    user_id: m.user_id, name: m.name, email: m.email, role: m.role,
    open: wsTasks.filter(t => assignedTo(t, m.user_id) && t.status !== 'done').length,
    total: wsTasks.filter(t => assignedTo(t, m.user_id)).length,
    overdue: wsTasks.filter(t => assignedTo(t, m.user_id) && isOverdue(t)).length,
    dueSoon: wsTasks.filter(t => assignedTo(t, m.user_id) && isDueSoon(t)).length,
  })).sort((a, b) => b.open - a.open);
  const unassignedOpen = wsTasks.filter(t => !t.assignee_user_id && !(t.assignees || []).length && t.status !== 'done').length;
  const maxOpen = Math.max(1, ...workload.map(w => w.open));
  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await teamsApi.sendDigest(activeTeam.id);
      toast.success(`Summary sent to ${res.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the summary'); }
    finally { setDigestBusy(false); }
  };
  const pagedMembers = activeTeam.members.slice((Math.min(memberPage, Math.max(1, Math.ceil(activeTeam.members.length / PAGE_SIZE))) - 1) * PAGE_SIZE, Math.min(memberPage, Math.max(1, Math.ceil(activeTeam.members.length / PAGE_SIZE))) * PAGE_SIZE);
  return (
    <div className="pagewrap home-v2" data-testid="workspace-page">
      <button onClick={() => setSelectedId(null)} data-testid="workspace-back-button"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ChevronLeft size={16} />All workspaces
      </button>
      <div className="greet-row">
        <div className="greet">
          {editingTitle ? (
            <input autoFocus value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
              onBlur={saveTitle} onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
              data-testid="workspace-title-input"
              className="serif bg-transparent border-b-2 border-[#5a7d52] outline-none"
              style={{ fontFamily: "'Instrument Serif',Georgia,serif", fontSize: 32, lineHeight: 1.04, color: 'var(--ink)', width: '100%', maxWidth: 520 }} />
          ) : (
            <h1 className="flex items-center gap-2 group" data-testid="workspace-name">
              {activeTeam.name}{activeTeam.is_owner && <Crown size={20} style={{ color: '#c79a3f' }} />}
              {activeTeam.is_owner && (
                <button onClick={() => { setTitleDraft(activeTeam.name); setEditingTitle(true); }} data-testid="workspace-title-edit"
                  title="Rename workspace" className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-secondary" style={{ color: '#9aa3b0' }}>
                  <Pencil size={15} />
                </button>
              )}
            </h1>
          )}
          <p>{activeTeam.is_owner ? 'You own this workspace' : 'You are a member'} · {activeTeam.members.length} member{activeTeam.members.length === 1 ? '' : 's'}</p>
        </div>
        {!isViewer && (
          <button className="btn-capture-top" onClick={() => setShowAddTask(true)} data-testid="workspace-add-task-header-button">
            <Plus size={16} />New task
          </button>
        )}
      </div>

      {/* Folder navigation — folders scope the entire workspace (tasks, activity, cards) */}
      {wsFolders.length > 0 && (
        <div className="ws-folderbar" data-testid="workspace-folder-bar">
          <div className="ws-crumbs" data-testid="workspace-breadcrumb">
            <button className={`crumb${!wsFolder ? ' on' : ''}${dropTarget === 'crumb-root' ? ' dropping' : ''}`} onClick={() => selectFolder(null)} data-testid="workspace-crumb-all"
              onDragOver={e => { if (draggingCard && wsFolder) { e.preventDefault(); setDropTarget('crumb-root'); } }}
              onDragLeave={() => setDropTarget(t => t === 'crumb-root' ? null : t)}
              onDrop={e => { e.preventDefault(); if (draggingCard) handleDrop(null); }}
              title={draggingCard ? 'Drop to move to the workspace root' : undefined}>
              All of {activeTeam.name}
            </button>
            {breadcrumb.map(f => (
              <span key={f.id} className="crumb-wrap">
                <ChevronRight size={13} className="crumb-sep" />
                <button className={`crumb${wsFolder === f.id ? ' on' : ''}${dropTarget === 'crumb-' + f.id ? ' dropping' : ''}`} onClick={() => selectFolder(f.id)} data-testid={`workspace-crumb-${f.id}`}
                  onDragOver={e => { if (draggingCard) { e.preventDefault(); setDropTarget('crumb-' + f.id); } }}
                  onDragLeave={() => setDropTarget(t => t === 'crumb-' + f.id ? null : t)}
                  onDrop={e => { e.preventDefault(); if (draggingCard) handleDrop(f.id); }}
                  title={draggingCard ? `Drop to move into ${f.name}` : undefined}>{f.name}</button>
              </span>
            ))}
          </div>
          <div className="ws-folderchips">
            {folderChildren(wsFolder).map(f => (
              <div key={f.id} className={`folderchip${dropTarget === 'chip-' + f.id ? ' dropping' : ''}`}
                onDragOver={e => { if (draggingCard) { e.preventDefault(); setDropTarget('chip-' + f.id); } }}
                onDragLeave={() => setDropTarget(t => t === 'chip-' + f.id ? null : t)}
                onDrop={e => { e.preventDefault(); if (draggingCard) handleDrop(f.id); }}
                title={draggingCard ? `Drop to move into ${f.name}` : undefined}>
                {renamingFolder === f.id ? (
                  <>
                    <Folder size={13} />
                    <input autoFocus value={renameDraft} onChange={e => setRenameDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') renameFolder(f); if (e.key === 'Escape') setRenamingFolder(null); }}
                      onBlur={() => renameFolder(f)}
                      data-testid={`workspace-folder-rename-input-${f.id}`}
                      className="chip-rename-input" />
                  </>
                ) : (
                  <>
                    <button className="chip-open" onClick={() => selectFolder(f.id)} data-testid={`workspace-folderchip-${f.id}`}>
                      {f.emoji ? <span className="fldr-emoji">{f.emoji}</span> : <Folder size={13} style={{ color: folderColorOf(f) }} />}<span className="nm">{f.name}</span><span className="cnt">{dropTarget === 'chip-' + f.id ? 'drop here' : folderCardCount(f.id)}</span>
                    </button>
                    {activeTeam.is_owner && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="chip-menu" title="Folder options" data-testid={`workspace-folder-menu-${f.id}`}><MoreHorizontal size={14} /></button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-52 p-1 bg-white" data-testid={`workspace-folder-menu-content-${f.id}`}>
                          <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary text-left text-sm"
                            onClick={() => { setRenamingFolder(f.id); setRenameDraft(f.name); }}
                            data-testid={`workspace-folder-rename-${f.id}`}><Pencil size={13} />Rename</button>
                          {!f.parent_id && (
                            <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary text-left text-sm"
                              onClick={() => { setSubfolderParent(f.id); setSubfolderName(''); }}
                              data-testid={`workspace-folder-new-subfolder-${f.id}`}><Folder size={13} />New subfolder</button>
                          )}
                          <div className="px-2 pt-1.5 pb-1">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Color</div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {FOLDER_COLORS.map(c => (
                                <button key={c} onClick={() => setFolderColor(f, c)} title="Set color"
                                  data-testid={`workspace-folder-color-${f.id}-${c.replace('#', '')}`}
                                  className="w-5 h-5 rounded-full transition-transform hover:scale-110"
                                  style={{ background: c, outline: folderColorOf(f) === c ? '2px solid var(--ink,#212E40)' : 'none', outlineOffset: 1 }} />
                              ))}
                            </div>
                          </div>
                          <div className="px-2 pt-1 pb-1.5">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Emoji</div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {FOLDER_EMOJIS.map(em => (
                                <button key={em} onClick={() => setFolderEmoji(f, em)} title="Set emoji"
                                  data-testid={`workspace-folder-emoji-${f.id}-${em}`}
                                  className="w-6 h-6 rounded-md flex items-center justify-center text-[15px] leading-none transition-transform hover:scale-110"
                                  style={{ background: f.emoji === em ? 'var(--tint,#E6EDF5)' : 'transparent' }}>{em}</button>
                              ))}
                            </div>
                          </div>
                          <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary text-left text-sm"
                            style={{ color: '#B4332E' }} onClick={() => setDeleteFolderTarget(f)}
                            data-testid={`workspace-folder-delete-${f.id}`}><Trash2 size={13} />Delete</button>
                        </PopoverContent>
                      </Popover>
                    )}
                  </>
                )}
              </div>
            ))}
            {!isViewer && (
              <button className="folderchip ghost" onClick={() => { setNewFolderOpen(true); setNewFolderName(''); }} data-testid="workspace-folderbar-new-folder">
                <Plus size={13} />New folder
              </button>
            )}
          </div>
          {subfolderParent && (
            <div className="ws-subfolder-create" data-testid="workspace-subfolder-create-row">
              <Folder size={14} style={{ color: '#b08900', flex: 'none' }} />
              <input autoFocus value={subfolderName} onChange={e => setSubfolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') createSubfolder(); if (e.key === 'Escape') { setSubfolderParent(null); setSubfolderName(''); } }}
                placeholder={`New subfolder in ${folderById[subfolderParent]?.name || ''}`}
                data-testid="workspace-subfolder-input"
                className="flex-1 h-9 px-3 rounded-lg border border-border bg-white text-sm outline-none focus:border-primary" />
              <button onClick={createSubfolder} disabled={!subfolderName.trim() || creatingSubfolder} data-testid="workspace-subfolder-create"
                className="h-9 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-40 inline-flex items-center gap-1" style={{ background: '#5a7d52' }}>
                {creatingSubfolder ? <Loader2 size={13} className="animate-spin" /> : <Folder size={13} />}Create
              </button>
              <button onClick={() => { setSubfolderParent(null); setSubfolderName(''); }} className="h-9 px-2 text-xs text-muted-foreground" data-testid="workspace-subfolder-cancel">Cancel</button>
            </div>
          )}
          {draggingCard && wsFolder && siblingFolders.length > 0 && (
            <div className="ws-sibling-drop" data-testid="workspace-sibling-drop">
              <span className="lbl">Move to sibling folder:</span>
              {siblingFolders.map(f => (
                <button key={f.id} className={`folderchip${dropTarget === 'sib-' + f.id ? ' dropping' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDropTarget('sib-' + f.id); }}
                  onDragLeave={() => setDropTarget(t => t === 'sib-' + f.id ? null : t)}
                  onDrop={e => { e.preventDefault(); if (draggingCard) handleDrop(f.id); }}
                  data-testid={`workspace-sibling-chip-${f.id}`}
                  title={`Drop to move into ${f.name}`}>
                  <span className="chip-open" style={{ pointerEvents: 'none' }}>{f.emoji ? <span className="fldr-emoji">{f.emoji}</span> : <Folder size={13} style={{ color: folderColorOf(f) }} />}<span className="nm">{f.name}</span></span>
                </button>
              ))}
            </div>
          )}
          {wsFolder && (
            <p className="ws-scope-note" data-testid="workspace-scope-note">
              {draggingCard
                ? <span style={{ color: '#5a7d52', fontWeight: 700 }}>Drop a card on a folder above to file it there.</span>
                : <>Showing tasks, activity &amp; cards in <strong>{openFolder?.name}</strong>{folderChildren(wsFolder).length > 0 ? ' and its subfolders' : ''}.</>}
            </p>
          )}
        </div>
      )}

      <div className="layout">
        <div className="col-main">
          <div className="ryg-bar hub" data-testid="workspace-hub-ryg-bar">
            <span className="ryg-lbl">Status</span>
            {RYG_ORDER.map(k => (
              <button key={k} className={`ryg-chip${rygFilter === k ? ' on' : ''}`} data-testid={`workspace-hub-ryg-${k}`}
                onClick={() => { setRygFilter(rygFilter === k ? null : k); setTaskPage(1); }}
                title={rygFilter === k ? 'Clear filter' : `Show ${RYG_META[k].label.toLowerCase()}`}
                style={rygFilter === k ? { borderColor: RYG_META[k].color, background: RYG_META[k].bg } : undefined}>
                <span className="ryg-dot" style={{ background: RYG_META[k].color }} />
                {RYG_META[k].label}<b>{hubRyg[k]}</b>
              </button>
            ))}
            {rygFilter && <button className="ryg-clear" data-testid="workspace-hub-ryg-clear" onClick={() => { setRygFilter(null); setTaskPage(1); }}><X size={13} />Clear</button>}
          </div>

          {/* Needs you today — tasks assigned to you in this workspace */}
          <CollapsibleSection
            id="needs-you"
            title={focusMember ? `${focusMember.name}'s tasks` : 'Needs you today'}
            actions={<>
              {focusMember ? (
                <span className="link" onClick={() => { setFocusMember(null); setTaskPage(1); }} data-testid="workspace-clear-member-filter">Clear filter</span>
              ) : (
                <span className="link" onClick={() => { setShowAllTasks(v => !v); setTaskPage(1); }} data-testid="workspace-toggle-all-tasks">
                  {showAllTasks ? 'Assigned to me' : `All tasks (${scopedWsTasks.length})`}
                </span>
              )}
              {!isViewer && <span className="link" onClick={() => setShowAddTask(v => !v)} data-testid="workspace-add-task-toggle">+ Add task</span>}
            </>}
          >
            {focusMember && (
              <div className="mb-2 flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg text-xs" data-testid="workspace-member-filter-banner"
                style={{ background: 'rgba(105,148,198,0.08)', color: '#3d6a9e' }}>
                <Users size={13} /><span>Showing tasks assigned to <strong>{focusMember.name}</strong></span>
                {activeTeam.members.filter(m => m.user_id !== focusMember.user_id).length > 0 && (
                  <span className="flex items-center gap-1.5 ml-auto">
                    <span className="text-muted-foreground">Reassign all to</span>
                    <select value={reassignTo} onChange={e => setReassignTo(e.target.value)} data-testid="workspace-reassign-select"
                      className="h-7 rounded-lg text-xs px-2 bg-white border border-border outline-none cursor-pointer">
                      <option value="">Choose member…</option>
                      {activeTeam.members.filter(m => m.user_id !== focusMember.user_id).map(m => (
                        <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                      ))}
                    </select>
                    <button onClick={bulkReassign} disabled={!reassignTo || reassigning} data-testid="workspace-reassign-button"
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40" style={{ background: '#6994c6' }}>
                      {reassigning ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}Reassign
                    </button>
                  </span>
                )}
              </div>
            )}
            <section className="card" data-testid="workspace-tasks-list">
              {showAddTask && (
                <form onSubmit={addWorkspaceTask} className="flex gap-2 p-4 border-b border-border" data-testid="workspace-add-task-form">
                  <input autoFocus value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)}
                    placeholder="What needs to get done in this workspace?" data-testid="workspace-task-input"
                    className="flex-1 h-10 px-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-[#5a7d52]" />
                  <button type="submit" disabled={addingTask || !newTaskTitle.trim()} data-testid="workspace-task-submit"
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-medium text-white disabled:opacity-50" style={{ background: '#5a7d52' }}>
                    {addingTask ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}Add
                  </button>
                </form>
              )}
              {listTasks.length === 0 ? (
                <div className="empty" data-testid="workspace-tasks-empty">
                  <div className="ei"><CheckSquare size={20} /></div>
                  <h4>{focusMember ? `Nothing assigned to ${focusMember.name}` : (showAllTasks ? 'No workspace tasks yet' : 'Nothing assigned to you')}</h4>
                  <p>{focusMember ? 'This member has no tasks in this workspace yet.' : (showAllTasks ? 'Add a task that lives in this workspace — no Action Card needed.' : "You're all caught up here. Switch to “All tasks” to see everything in this workspace.")}</p>
                </div>
              ) : (
                pagedTasks.map(t => {
                  const done = t.status === 'done';
                  const isExpanded = expandedTaskId === t.id;
                  const pri = t.priority === 'high' ? { cls: 'due', label: 'High' } : { cls: 'today', label: t.priority === 'low' ? 'Low' : 'Med' };
                  return (
                    <div key={t.id} data-testid={`workspace-task-container-${t.id}`}>
                    <div className={`task${done ? ' done' : ''}`} data-testid={`workspace-task-${t.id}`}>
                      <div className={`ck${done ? ' done' : ''}`} onClick={() => toggleWorkspaceTask(t)} data-testid={`workspace-task-checkbox-${t.id}`}>
                        <Check size={12} />
                      </div>
                      <div className="tbody" onClick={() => openTaskDrawer(t.id, { onChanged: reloadHub })} style={{ cursor: 'pointer' }} data-testid={`workspace-task-title-toggle-${t.id}`}>
                        <div className="tt">{t.title}</div>
                        <div className="ts">{t.task_ref} · {t.assignee_name ? `→ ${t.assignee_name}` : (t.owner_name || t.owner)}{t.due_date ? ` · ${t.due_date}` : ''}{(t.collaborators || []).length ? ` · +${t.collaborators.length} collaborator${t.collaborators.length > 1 ? 's' : ''}` : ''}</div>
                      </div>
                      <div className="tmeta">
                        <span className={`pill ${done ? 'done' : pri.cls}`}>{done ? 'Done' : pri.label}</span>
                        {isViewer ? (
                          <span title={t.assignee_name ? `Assigned to ${t.assignee_name}` : 'Unassigned'} data-testid={`workspace-task-assignee-${t.id}`}>
                            {t.assignee_user_id ? (
                              <span className="w-6 h-6 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: '#6994c6' }}>{t.assignee_initials || initials(t.assignee_name)}</span>
                            ) : (
                              <span className="w-6 h-6 rounded-full border border-dashed border-border flex items-center justify-center" style={{ color: '#9aa3b0' }}><UserPlus size={12} /></span>
                            )}
                          </span>
                        ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button title={t.assignee_name ? `Assigned to ${(t.assignees || []).map(a => a.name).join(', ') || t.assignee_name}` : 'Assign to a member'} data-testid={`workspace-task-assign-${t.id}`}>
                              {wsAssigneeIds(t).length ? (
                                <span className="inline-flex items-center">
                                  <span className="w-6 h-6 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: '#6994c6' }}>{t.assignee_initials || initials(t.assignee_name)}</span>
                                  {wsAssigneeIds(t).length > 1 && <span className="w-6 h-6 -ml-2 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: '#e7e3da', color: '#6b7482', border: '1.5px solid #fff' }}>+{wsAssigneeIds(t).length - 1}</span>}
                                </span>
                              ) : (
                                <span className="w-6 h-6 rounded-full border border-dashed border-border flex items-center justify-center" style={{ color: '#9aa3b0' }}><UserPlus size={12} /></span>
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-56 p-1 bg-white" data-testid={`workspace-task-assign-menu-${t.id}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground px-2 py-1.5">Assignees — tap to toggle</p>
                            {activeTeam.members.map((m, mi) => (
                              <button key={m.user_id || m.email || mi} onClick={() => toggleWsAssignee(t, m.user_id)} data-testid={`ws-assign-option-${t.id}-${m.user_id}`}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary text-left text-sm">
                                <span className="w-6 h-6 rounded-full text-[10px] font-bold text-white flex items-center justify-center shrink-0" style={{ background: m.role === 'owner' ? '#5a7d52' : '#6994c6' }}>{initials(m.name)}</span>
                                <span className="flex-1 truncate">{m.name || m.email}</span>
                                {wsAssigneeIds(t).includes(m.user_id) && <Check size={13} style={{ color: '#6994c6' }} />}
                              </button>
                            ))}
                            {wsAssigneeIds(t).length > 0 && (
                              <button onClick={() => assignWorkspaceTask(t, [])} data-testid={`ws-assign-clear-${t.id}`}
                                className="w-full text-left px-2 py-1.5 mt-0.5 rounded-lg hover:bg-secondary text-sm" style={{ color: '#B4332E' }}>Unassign everyone</button>
                            )}
                          </PopoverContent>
                        </Popover>
                        )}
                        {!isViewer && (
                        <button onClick={() => deleteWorkspaceTask(t)} title="Delete task" data-testid={`workspace-task-delete-${t.id}`}
                          className="p-1 rounded-lg hover:bg-secondary" style={{ color: '#9aa3b0' }}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </div>
                    </div>
                  );
                })
              )}
              <Pager page={taskPage} total={listTasks.length} onChange={setTaskPage} testid="workspace-tasks-pager" />
            </section>
          </CollapsibleSection>

          {/* Cards in this workspace — Action Cards shared with the team */}
          <CollapsibleSection
            id="cards"
            title={<>
              {(wsFolder || showAllCards) && (
                <button onClick={(e) => { e.stopPropagation(); if (showAllCards) { setShowAllCards(false); } else { setWsFolder(openFolder?.parent_id || null); } }} data-testid="workspace-folder-back"
                  onDragOver={e => { if (draggingCard && !showAllCards) { e.preventDefault(); setDropTarget('__root__'); } }}
                  onDragLeave={() => setDropTarget(t => t === '__root__' ? null : t)}
                  onDrop={e => { e.preventDefault(); if (!showAllCards) handleDrop(openFolder?.parent_id || null); }}
                  className="inline-flex items-center text-muted-foreground hover:text-foreground rounded"
                  style={{ outline: dropTarget === '__root__' ? '2px dashed #5a7d52' : 'none', outlineOffset: 2 }}
                  title={draggingCard ? 'Drop to move up a level' : 'Back'}>
                  <ChevronLeft size={14} />
                </button>
              )}
              {showAllCards ? 'All cards in this workspace' : (wsFolder ? (openFolder?.name || 'Folder') : 'Cards in this workspace')}
            </>}
            actions={<div className="flex items-center gap-1" data-testid="workspace-card-filters">
              {[['all', 'All'], ['open', 'Open'], ['done', 'Done']].map(([key, label]) => (
                <button key={key} onClick={() => setCardFilter(key)}
                  data-testid={`workspace-card-filter-${key}`}
                  className="px-2.5 h-7 rounded-full text-xs font-semibold transition-colors"
                  style={cardFilter === key
                    ? { background: '#5a7d52', color: '#fff' }
                    : { background: 'transparent', color: '#6b7482', border: '1px solid rgba(33,46,64,.14)' }}>
                  {label}
                </button>
              ))}
              <select value={cardSort} onChange={e => setCardSort(e.target.value)} data-testid="workspace-card-sort"
                className="h-7 rounded-full text-xs font-semibold px-2 outline-none cursor-pointer"
                style={{ background: 'transparent', color: '#6b7482', border: '1px solid rgba(33,46,64,.14)' }}>
                <option value="recent">Recently updated</option>
                <option value="tasks">Most tasks</option>
                <option value="owner">Owner</option>
              </select>
              <span className="link ml-1" onClick={() => { setShowAllCards(v => !v); setWsFolder(null); setCardFilter('all'); setLegendFilter(null); }} data-testid="workspace-cards-view-all">{showAllCards ? 'By folder' : 'View all'}</span>
              {!isViewer && <span className="link ml-1 inline-flex items-center gap-0.5" onClick={() => { setNewFolderOpen(v => !v); setNewFolderName(''); }} data-testid="workspace-new-folder-toggle"><Plus size={12} />New folder</span>}
            </div>}
          >
            {newFolderOpen && (
              <div className="mb-2 flex items-center gap-2" data-testid="workspace-new-folder-row">
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setNewFolderOpen(false); }}
                  placeholder={wsFolder ? `New folder inside ${openFolder?.name || ''}` : 'New folder name'}
                  data-testid="workspace-new-folder-input"
                  className="flex-1 h-9 px-3 rounded-lg border border-border bg-white text-sm outline-none focus:border-primary" />
                <button onClick={createFolder} disabled={!newFolderName.trim() || creatingFolder} data-testid="workspace-new-folder-create"
                  className="h-9 px-3 rounded-lg text-xs font-semibold text-white disabled:opacity-40 inline-flex items-center gap-1" style={{ background: '#5a7d52' }}>
                  {creatingFolder ? <Loader2 size={13} className="animate-spin" /> : <Folder size={13} />}Create
                </button>
                <button onClick={() => setNewFolderOpen(false)} className="h-9 px-2 text-xs text-muted-foreground">Cancel</button>
              </div>
            )}
            {showAllCards && wsFolders.some(f => (f.color || f.emoji) && folderCardCount(f.id) > 0) && (
              <div className="ws-legend" data-testid="workspace-color-legend">
                <span className="ws-legend-lbl">Folders</span>
                {wsFolders.filter(f => (f.color || f.emoji) && folderCardCount(f.id) > 0).map(f => (
                  <button key={f.id} className={`ws-legend-item${legendFilter === f.id ? ' on' : ''}`} data-testid={`workspace-legend-${f.id}`}
                    onClick={() => setLegendFilter(legendFilter === f.id ? null : f.id)}
                    title={legendFilter === f.id ? 'Clear filter' : `Show only ${f.name}`}>
                    {f.emoji ? <span className="em">{f.emoji}</span> : <span className="dot" style={{ background: folderColorOf(f) }} />}
                    {f.name}
                  </button>
                ))}
              </div>
            )}
            {selectedCards.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg text-xs" data-testid="workspace-bulk-bar" style={{ background: 'rgba(105,148,198,0.1)', color: '#3d6a9e' }}>
                <CheckSquare size={13} /><span><strong>{selectedCards.length}</strong> selected</span>
                <span className="flex items-center gap-1.5 ml-auto">
                  <span className="text-muted-foreground">Move to</span>
                  <select value={bulkMoveTo} onChange={e => setBulkMoveTo(e.target.value)} data-testid="workspace-bulk-move-select"
                    className="h-7 rounded-lg text-xs px-2 bg-white border border-border outline-none cursor-pointer">
                    <option value="">Workspace root</option>
                    {wsFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <button onClick={bulkMove} data-testid="workspace-bulk-move-button" className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-semibold text-white" style={{ background: '#5a7d52' }}><Folder size={12} />Move</button>
                  <button onClick={() => setSelectedCards([])} data-testid="workspace-bulk-clear" className="text-muted-foreground font-medium">Clear</button>
                </span>
              </div>
            )}
            <section className="card" data-testid="workspace-cards-list">
              {currentFolders.length === 0 && visibleCards.length === 0 ? (
                <div className="empty" data-testid="workspace-cards-empty">
                  <div className="ei"><FileText size={20} /></div>
                  <h4>{activity.length === 0 && wsFolders.length === 0 ? 'No cards shared here yet' : (wsFolder ? 'This folder is empty' : 'Nothing here')}</h4>
                  <p>{activity.length === 0 && wsFolders.length === 0 ? 'Move an Action Card into this workspace to share it with everyone on the team.' : 'No cards match this filter. Try another.'}</p>
                </div>
              ) : (
                <>
                  {cardFilter === 'all' && currentFolders.map(f => (
                    <div key={f.id} className="task" onClick={() => { setWsFolder(f.id); setCardFilter('all'); setSelectedCards([]); }}
                      onDragOver={e => { if (draggingCard) { e.preventDefault(); setDropTarget(f.id); } }}
                      onDragLeave={() => setDropTarget(t => t === f.id ? null : t)}
                      onDrop={e => { e.preventDefault(); handleDrop(f.id); }}
                      style={{ cursor: 'pointer', outline: dropTarget === f.id ? '2px dashed #5a7d52' : 'none', outlineOffset: 2, borderRadius: 10, background: dropTarget === f.id ? 'rgba(90,125,82,0.06)' : undefined }}
                      data-testid={`workspace-folder-${f.id}`}>
                      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${folderColorOf(f)}22`, color: folderColorOf(f) }}>{f.emoji ? <span style={{ fontSize: 15, lineHeight: 1 }}>{f.emoji}</span> : <Folder size={15} />}</span>
                      <div className="tbody">
                        <div className="tt">{f.name}</div>
                        <div className="ts">{folderCardCount(f.id)} card{folderCardCount(f.id) === 1 ? '' : 's'}{dropTarget === f.id ? ' · drop to move here' : ''}</div>
                      </div>
                      <div className="tmeta"><ChevronRight size={16} className="text-muted-foreground shrink-0" /></div>
                    </div>
                  ))}
                  {visibleCards.map(c => {
                  const total = c.total_tasks || 0, open = c.open_tasks || 0;
                  const stage = total === 0 ? { label: 'No tasks', cls: 'today' }
                    : open === 0 ? { label: 'All done', cls: 'done' }
                    : { label: `${open} open`, cls: open > 0 ? 'due' : 'today' };
                  const cardFolder = c.folder_id ? wsFolders.find(f => f.id === c.folder_id) : null;
                  const cardStrip = cardFolder ? folderColorOf(cardFolder) : null;
                  return (
                  <div key={c.id} className="task"
                    draggable={!isViewer}
                    onDragStart={e => { setDraggingCard(c.id); try { e.dataTransfer.setData('text/cardId', c.id); e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ } }}
                    onDragEnd={() => { setDraggingCard(null); setDropTarget(null); }}
                    onClick={() => navigate(`/action-cards/${c.id}`)}
                    style={{ position: 'relative', cursor: 'pointer', opacity: draggingCard === c.id ? 0.5 : 1, background: selectedCards.includes(c.id) ? 'rgba(105,148,198,0.08)' : undefined, borderRadius: 10 }}
                    data-testid={`workspace-card-item-${c.id}`}>
                    {cardStrip && <span className="row-strip" style={{ background: cardStrip }} data-testid={`workspace-card-strip-${c.id}`} title={cardFolder?.name} />}
                    {!isViewer && (
                      <button onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}
                        className="w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors"
                        style={{ borderColor: selectedCards.includes(c.id) ? '#6994c6' : 'rgba(33,46,64,.22)', background: selectedCards.includes(c.id) ? '#6994c6' : 'transparent' }}
                        title="Select" data-testid={`workspace-card-select-${c.id}`}>
                        {selectedCards.includes(c.id) && <Check size={13} className="text-white" />}
                      </button>
                    )}
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(90,125,82,0.12)', color: '#5a7d52' }}><FileText size={14} /></span>
                    <div className="tbody">
                      <div className="tt">{cardRYG(c.id) && <span className="ryg-dot" title={RYG_META[cardRYG(c.id)].label} style={{ background: RYG_META[cardRYG(c.id)].color, marginRight: 6 }} />}{cardFolder?.emoji && <span style={{ marginRight: 5 }} title={cardFolder.name}>{cardFolder.emoji}</span>}{c.title || c.contact_name || 'Untitled card'}</div>
                      <div className="ts">{showAllCards && c.folder_id ? `${wsFolders.find(f => f.id === c.folder_id)?.name || 'Folder'} · ` : ''}{c.owner_name || 'Someone'} · {total ? `${total} task${total === 1 ? '' : 's'}` : 'no tasks'} · {relTime(c.updated_at || c.created_at)}</div>
                    </div>
                    <div className="tmeta" onClick={e => e.stopPropagation()}>
                      {(wsFolders.length > 0 || c.folder_id) && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" title="Move to folder" data-testid={`workspace-card-move-${c.id}`}>
                              <Folder size={14} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-52 p-1" data-testid={`workspace-move-menu-${c.id}`}>
                            <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Move to</div>
                            {(c.folder_id || null) !== null && (
                              <button onClick={() => moveCardToFolder(c.id, null)} className="w-full text-left px-2 py-1.5 rounded-md text-sm hover:bg-secondary flex items-center gap-2" data-testid={`workspace-move-root-${c.id}`}>
                                <Inbox size={13} className="text-muted-foreground" />Workspace root
                              </button>
                            )}
                            {wsFolders.filter(f => f.id !== (c.folder_id || null)).map(f => (
                              <button key={f.id} onClick={() => moveCardToFolder(c.id, f.id)} className="w-full text-left px-2 py-1.5 rounded-md text-sm hover:bg-secondary flex items-center gap-2" data-testid={`workspace-move-to-${f.id}-${c.id}`}>
                                {f.emoji ? <span style={{ fontSize: 13, lineHeight: 1 }}>{f.emoji}</span> : <Folder size={13} style={{ color: folderColorOf(f) }} />}{f.name}
                              </button>
                            ))}
                          </PopoverContent>
                        </Popover>
                      )}
                      {(c.overdue_tasks || 0) > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: '#B4332E' }} data-testid={`workspace-card-overdue-${c.id}`}>{c.overdue_tasks} overdue</span>
                      )}
                      {(c.due_soon_tasks || 0) > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }} data-testid={`workspace-card-duesoon-${c.id}`}>{c.due_soon_tasks} due</span>
                      )}
                      <span className={`pill ${stage.cls}`} data-testid={`workspace-card-stage-${c.id}`}>{stage.label}</span>
                      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                    </div>
                  </div>
                  );
                })}
                </>
              )}
            </section>
          </CollapsibleSection>

          {/* Recent activity — audit trail of tasks assigned to you */}
          <CollapsibleSection
            id="recent-activity"
            title={<>
              Recent activity
              {unreadActivity > 0 && (
                <span data-testid="workspace-activity-unread"
                  className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white"
                  style={{ background: '#B4332E' }} title={`${unreadActivity} new update${unreadActivity === 1 ? '' : 's'} from teammates`}>
                  {unreadActivity}
                </span>
              )}
            </>}
            actions={myActivity.length > 0 ? (
              <div className="flex items-center gap-1" data-testid="workspace-activity-filters">
                {[['all', 'All'], ['assign', 'Assignments'], ['status', 'Status'], ['comment', 'Comments']].map(([key, label]) => (
                  <button key={key} onClick={() => { setActivityFilter(key); setActivityPage(1); markActivitySeen(); }}
                    data-testid={`workspace-activity-filter-${key}`}
                    className="px-2.5 h-7 rounded-full text-xs font-semibold transition-colors"
                    style={activityFilter === key
                      ? { background: '#6994c6', color: '#fff' }
                      : { background: 'transparent', color: '#6b7482', border: '1px solid rgba(33,46,64,.14)' }}>
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          >
            <section className="card" data-testid="workspace-activity-card" onClick={markActivitySeen}>
              {filteredActivity.length === 0 ? (
                <div className="empty" data-testid="workspace-activity-empty">
                  <div className="ei"><Activity size={20} /></div>
                  <h4>{myActivity.length === 0 ? 'No activity yet' : 'Nothing here'}</h4>
                  <p>{myActivity.length === 0 ? "Updates to tasks you're assigned to — status changes, comments and more — will show up here." : 'No activity matches this filter yet. Try another.'}</p>
                </div>
              ) : (
                <div data-testid="workspace-activity-list">
                  {pagedActivity.map((ev, i) => {
                    const Icon = ev.type === 'comment' ? MessageSquare : ev.type === 'status' ? CheckSquare : ev.type === 'assign' ? UserPlus : Users;
                    return (
                      <div key={`${ev.task_id}-${i}`} className="task" onClick={() => { const t = wsTasks.find(x => x.id === ev.task_id); if (t) setExpandedTaskId(t.id); }} data-testid={`workspace-activity-item-${i}`}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(105,148,198,0.12)', color: '#6994c6' }}><Icon size={14} /></span>
                        <div className="tbody">
                          <div className="tt"><span style={{ fontWeight: 600 }}>{ev.actor_name}</span> {ev.type === 'comment' ? 'commented' : ev.detail} {ev.type === 'comment' && ev.detail ? `— “${ev.detail}”` : ''}</div>
                          <div className="ts">{ev.task_ref ? `${ev.task_ref} · ` : ''}{ev.task_title} · {relTime(ev.at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Pager page={activityPage} total={filteredActivity.length} onChange={setActivityPage} testid="workspace-activity-pager" />
            </section>
          </CollapsibleSection>

          {/* Workload — moved to the bottom of the main column */}
          <CollapsibleSection
            id="workload"
            title="Workload"
            actions={activeTeam.is_owner ? (
              <button onClick={sendDigest} disabled={digestBusy} data-testid="workspace-send-digest-button"
                className="link inline-flex items-center gap-1" title="Email yourself this week's summary">
                {digestBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}Email summary
              </button>
            ) : null}
          >
            <section className="card" style={{ padding: 18 }} data-testid="workspace-workload-card">
              <p className="text-xs text-muted-foreground mb-3">Open tasks assigned to each member in this workspace.</p>
              <div className="space-y-2.5">
                {workload.map(w => (
                  <div key={w.user_id} onClick={() => { setFocusMember(f => f?.user_id === w.user_id ? null : { user_id: w.user_id, name: w.name || w.email }); setTaskPage(1); }}
                    className="flex items-center gap-3 rounded-lg px-1.5 py-1 -mx-1.5 cursor-pointer transition-colors hover:bg-secondary/40"
                    style={focusMember?.user_id === w.user_id ? { background: 'rgba(105,148,198,0.12)' } : undefined}
                    title={`Show ${w.name || w.email}'s tasks`} data-testid={`workload-row-${w.user_id}`}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: w.role === 'owner' ? '#5a7d52' : '#6994c6' }}>{initials(w.name)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{w.name || w.email}</span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {w.overdue > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: '#B4332E' }} data-testid={`workload-overdue-${w.user_id}`}>{w.overdue} overdue</span>
                          )}
                          {w.dueSoon > 0 && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }} data-testid={`workload-duesoon-${w.user_id}`}>{w.dueSoon} due</span>
                          )}
                          <span className="text-xs font-semibold" style={{ color: w.open > 0 ? '#212E40' : '#9aa3b0' }} data-testid={`workload-count-${w.user_id}`}>{w.open} open</span>
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(33,46,64,.08)' }}>
                        <div style={{ width: `${Math.round((w.open / maxOpen) * 100)}%`, height: '100%', background: w.overdue > 0 ? '#c2553f' : (w.open > 0 ? '#6994c6' : 'transparent'), borderRadius: 999, transition: 'width .3s ease' }} />
                      </div>
                    </div>
                  </div>
                ))}
                {unassignedOpen > 0 && (
                  <div className="flex items-center justify-between pt-1.5 text-xs" data-testid="workload-unassigned" style={{ color: '#B4332E' }}>
                    <span className="font-medium">Unassigned</span>
                    <span className="font-semibold">{unassignedOpen} open</span>
                  </div>
                )}
              </div>
            </section>
          </CollapsibleSection>

          {/* Members — moved to the bottom of the main column */}
          <CollapsibleSection id="members" title={`Members (${activeTeam.members.length})`}>
            <section className="card" style={{ padding: 18 }} data-testid="workspace-members-card">
              <p className="text-xs text-muted-foreground mb-3">Members can view &amp; edit anything placed in this workspace.</p>
              {activeTeam.is_owner && (
                <form onSubmit={invite} className="flex gap-2 mb-4">
                  <div className="relative flex-1">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="teammate@company.com"
                      data-testid="workspace-invite-input"
                      className="w-full h-10 pl-9 pr-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-[#6994c6]" />
                  </div>
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-testid="workspace-invite-role"
                    className="h-10 px-2 rounded-xl border border-border bg-white text-sm outline-none shrink-0">
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button type="submit" disabled={inviting || !email.trim()} data-testid="workspace-invite-button"
                    className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl text-sm font-medium text-white disabled:opacity-50" style={{ background: '#6994c6' }}>
                    {inviting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}Invite
                  </button>
                </form>
              )}
              <div className="space-y-1.5">
                {pagedMembers.map((m, mi) => (
                  <div key={m.user_id || m.email || mi} className="flex items-center gap-3 p-2 rounded-xl hover:bg-secondary/50" data-testid={`member-row-${m.user_id}`}>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: m.role === 'owner' ? '#5a7d52' : '#6994c6' }}>{initials(m.name)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium flex items-center gap-1.5">{m.name || m.email}{m.role === 'owner' && <Crown size={12} style={{ color: '#c79a3f' }} />}
                        {m.role === 'viewer' && <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(154,163,176,.15)', color: '#7c8694' }} data-testid={`member-role-${m.user_id}`}>Viewer</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                    </div>
                    {activeTeam.is_owner && m.role !== 'owner' && (
                      <button onClick={() => remove(activeTeam.id, m.user_id)} title="Remove" data-testid={`member-remove-${m.user_id}`}
                        className="p-1.5 rounded-lg hover:bg-secondary" style={{ color: '#9aa3b0' }}><X size={15} /></button>
                    )}
                  </div>
                ))}
                {(activeTeam.pending_invites || []).map(pi => (
                  <div key={pi.user_id} className="flex items-center gap-3 p-2 rounded-xl" data-testid={`pending-invite-${pi.user_id}`}>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-secondary" style={{ color: '#9aa3b0' }}><Clock size={14} /></span>
                    <div className="min-w-0 flex-1"><p className="text-sm text-muted-foreground truncate">{pi.name || pi.email}</p><p className="text-xs text-muted-foreground">Invited{pi.role === 'viewer' ? ' as Viewer' : ''} — awaiting acceptance</p></div>
                  </div>
                ))}
                {(activeTeam.pending_emails || []).map(pe => {
                  const meta = (activeTeam.pending_email_meta || []).find(m => m.email === pe);
                  return (
                  <div key={pe} className="flex items-center gap-3 p-2 rounded-xl" data-testid={`pending-row-${pe}`}>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-secondary" style={{ color: '#9aa3b0' }}><Mail size={14} /></span>
                    <div className="min-w-0 flex-1"><p className="text-sm text-muted-foreground truncate">{pe}</p><p className="text-xs text-muted-foreground">Invited{meta?.role === 'viewer' ? ' as Viewer' : ''} — will join when they sign up</p></div>
                  </div>
                  );
                })}
              </div>
              <Pager page={memberPage} total={activeTeam.members.length} onChange={setMemberPage} testid="workspace-members-pager" />
              {!activeTeam.is_owner && (
                <button onClick={() => remove(activeTeam.id, user.id)} data-testid={`leave-team-${activeTeam.id}`}
                  className="mt-4 text-xs font-medium" style={{ color: '#B4332E' }}>Leave this workspace</button>
              )}
              {activeTeam.is_owner && (
                <button onClick={() => { setDeleteConfirm(''); setShowDelete(true); }} data-testid="workspace-delete-button"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#B4332E' }}>
                  <Trash2 size={13} />Delete this workspace
                </button>
              )}
            </section>
          </CollapsibleSection>
        </div>
      </div>

      <Dialog open={!!deleteFolderTarget} onOpenChange={(o) => { if (!deletingFolder && !o) setDeleteFolderTarget(null); }}>
        <DialogContent data-testid="workspace-folder-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#B4332E' }}>
              <Trash2 size={18} />Delete folder
            </DialogTitle>
            <DialogDescription>
              Delete <strong>{deleteFolderTarget?.name}</strong>?
              {deleteFolderTarget && (() => {
                const cards = branchCardCount(deleteFolderTarget.id);
                const subs = folderChildren(deleteFolderTarget.id).length;
                return (
                  <> {cards > 0 ? `Its ${cards} card${cards === 1 ? '' : 's'} will move back to the workspace root (they stay in this workspace). ` : 'It has no cards. '}
                  {subs > 0 ? `${subs} subfolder${subs === 1 ? '' : 's'} will also be removed. ` : ''}This can’t be undone.</>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setDeleteFolderTarget(null)} disabled={deletingFolder} data-testid="workspace-folder-delete-cancel"
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button onClick={confirmDeleteFolder} disabled={deletingFolder} data-testid="workspace-folder-delete-confirm"
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold h-10 px-4 rounded-xl text-white transition-colors disabled:opacity-40"
              style={{ background: '#B4332E' }}>
              {deletingFolder ? <><Loader2 size={14} className="animate-spin" />Deleting…</> : <><Trash2 size={14} />Delete folder</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDelete} onOpenChange={(o) => { if (!deleting) { setShowDelete(o); if (!o) setDeleteConfirm(''); } }}>
        <DialogContent data-testid="workspace-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#B4332E' }}>
              <AlertTriangle size={18} />Delete workspace
            </DialogTitle>
            <DialogDescription>
              This removes <strong>{activeTeam?.name}</strong> for everyone and moves it to Trash. Shared Action Cards
              return to their owners meanwhile. You can restore everything from Home for <strong>30 days</strong> — after that it’s gone for good.
            </DialogDescription>
          </DialogHeader>
          <div className="py-1">
            <label className="text-xs font-medium text-muted-foreground">Type <strong>{activeTeam?.name}</strong> to confirm</label>
            <input
              autoFocus
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && deleteConfirm.trim() === activeTeam?.name) deleteWorkspace(); }}
              data-testid="workspace-delete-confirm-input"
              placeholder={activeTeam?.name}
              className="mt-1.5 w-full h-11 px-3 rounded-xl border border-border bg-white text-sm outline-none focus:border-[#B4332E]"
            />
          </div>
          <DialogFooter>
            <button onClick={() => { setShowDelete(false); setDeleteConfirm(''); }} disabled={deleting} data-testid="workspace-delete-cancel"
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button onClick={deleteWorkspace} disabled={deleting || deleteConfirm.trim() !== activeTeam?.name} data-testid="workspace-delete-confirm"
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold h-10 px-4 rounded-xl text-white transition-colors disabled:opacity-40"
              style={{ background: '#B4332E' }}>
              {deleting ? <><Loader2 size={14} className="animate-spin" />Deleting…</> : <><Trash2 size={14} />Delete workspace</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteOnCreateDialog
        team={inviteTeam}
        open={!!inviteTeam}
        onDone={() => { setInviteTeam(null); load(true); }}
      />
    </div>
  );
}
